<script>
import HubspotForm from '../../../../Forms/code/src/components/HubspotForm.vue';
import HereMaps from '../../../../../Project/Common/code/src/scripts/maps/HereMaps.js';
import HttpClient from '../helpers/HttpClient.js';
import IconChevron from './IconChevron.vue';

const hereAPI = new HereMaps();

export default {
    name: 'SeriesSelectorHubspotForm',
    components: { IconChevron, HubspotForm },
    props: {
        title: { type: String, default: null },
        answersText: { type: String, default: null },
        resultsText: { type: String, default: null },
        answers: { type: Array, default: () => [] },
        previousButtonText: { type: String, default: null },
        portalId: { type: String, required: true },
        formId: { type: String, required: true },
        dealerTypes: { type: String, default: '' },
        fieldNames: { type: Object, default: () => ({}) },
        locale: { type: String, required: true },
        initialCountry: { type: Object, default: null },
        countries: { type: Array, default: () => [] },
        range: { type: String, required: true },
        results: { type: Array, default: () => [] },
    },
    emits: ['select-previous-step', 'form-submitted'],
    data() {
        return {
            httpClient: null,
            form: null,
            fields: {},
            dealers: [],
            selectedCountryCode: '',
            selectedZipCode: '',
            selectedDealerCode: '',
        };
    },
    computed: {
        countryCode() {
            return this.initialCountry ? this.initialCountry.countryCode : this.selectedCountryCode;
        },
    },
    watch: {
        answers(updatedAnswers) {
            this.addAnswersToForm(updatedAnswers);
        },
        selectedCountryCode(newValue) {
            this.selectedCountryCode = newValue;
            if (this.selectedZipCode) {
                this.populateDealersWithZipCode({
                    zipCode: this.selectedZipCode,
                    countryCode: newValue,
                    range: this.range,
                    dealerTypes: this.dealerTypes,
                });
            } else {
                this.populateDealers({
                    countryCode: newValue,
                    range: this.range,
                    dealerTypes: this.dealerTypes,
                });
            }
        },
        selectedZipCode(newValue) {
            if (newValue.length === 0) {
                this.populateDealers({
                    countryCode: this.selectedCountryCode,
                    range: this.range,
                    dealerTypes: this.dealerTypes,
                });
            } else {
                this.populateDealersWithZipCode({
                    zipCode: newValue,
                    countryCode: this.selectedCountryCode,
                    range: this.range,
                    dealerTypes: this.dealerTypes,
                });
            }
        },
    },
    mounted() {
        this.httpClient = new HttpClient(`/${this.locale}/api/feature/forms`);
    },
    methods: {
        async onFormReady(hubspotForm) {
            this.form = hubspotForm;
            this.storeFormFields();
            this.initCountryField();
            this.initZipCodeField();
            await this.initDealerField();
        },
        storeFormFields() {
            Object.entries({
                segment: 'series_selector_segment',
                application: 'series_selector_application',
                axle: 'series_selector_wheelstep',
                chassis: 'series_selector_chassis',
                ...this.fieldNames,
            }).forEach(([key, name]) => {
                const field = this.form.find(`[name="${name}"]`);
                if (field.length > 0) {
                    this.fields[key] = field;
                } else {
                    throw new Error(`${key} field with name "${name}" was not found`);
                }
            });
        },
        initCountryField() {
            const countrySelect = this.fields.country;
            // remove all options except for the first
            countrySelect.find('option').not(':first').remove();
            if (this.initialCountry && this.initialCountry.countryCode) {
                const { country, countryCode } = this.initialCountry;
                // add by default selected option with initial country name
                countrySelect.append($('<option>', { selected: true, value: countryCode, text: country })).change();
            } else {
                // populate select with available countries
                this.countries.forEach(({ country, countryCode }) => {
                    countrySelect.append($('<option>', { value: countryCode, text: country }));
                });
                countrySelect.change((e) => {
                    // will trigger watcher
                    this.selectedCountryCode = e.target.value;
                });
            }
        },
        initZipCodeField() {
            const zipCodeInput = this.fields.zipCode;
            zipCodeInput.blur(() => {
                // will trigger watcher
                this.selectedZipCode = zipCodeInput.val();
            });
        },
        async initDealerField() {
            await this.populateDealers({
                dealerTypes: this.dealerTypes,
                range: this.range,
                countryCode: this.initialCountry.countryCode,
            });
            const dealerSelect = this.fields.dealer;
            dealerSelect.change((e) => {
                // will trigger watcher
                this.selectedDealerCode = e.target.value;
            });
        },
        async populateDealersWithZipCode({ zipCode, countryCode, ...rest }) {
            const { lat, lng } = await this.getLatLng(zipCode, countryCode);
            await this.populateDealers({ latitude: lat, longitude: lng, countryCode, ...rest });
        },
        async populateDealers(data) {
            const dealerSelect = this.fields.dealer;
            const result = await this.httpClient.post('/getdealers', data);
            if (result && result.length > 0) {
                // save dealers to later obtain
                this.dealers = result;
                // remove all options except for the first
                dealerSelect.find('option').not(':first').remove();
                // populate select
                this.dealers.forEach(({ Value, Text }) => {
                    dealerSelect.append($('<option>', { value: Value, text: Text }));
                });
                if (this.selectedDealerCode) {
                    // we don't want to trigger the change when no dealer is selected
                    // because it will result in a (required) validation error for the select
                    dealerSelect.change();
                }
            }
        },
        async getLatLng(zipCode, countryCode) {
            try {
                return await hereAPI.getGeoLocation(countryCode, null, zipCode);
            } catch (error) {
                console.error(error);
            }
        },
        addAnswersToForm(updatedAnswers) {
            for (const answer of updatedAnswers) {
                const stepName = answer.stepName.toLowerCase();
                if (stepName in this.fields) {
                    this.fields[stepName].val(answer.answer).change();
                }
            }
        },
        onFormSubmitted() {
            this.$emit('form-submitted');
        },
    },
};
</script>

<template>
    <div class="series-selector__contact-form">
        <div class="text row">
            <div class="col-12 col-md-6">
                <h3 class="results-title h2 font-weight-bold">{{ title }}</h3>
            </div>
        </div>
        <div class="border-bottom mb-4">
            <button class="previous d-flex align-items-center" @click="$emit('select-previous-step')">
                <IconChevron />
                <span>{{ previousButtonText }}</span>
            </button>
        </div>
        <div class="row align-items-start flex-wrap-reverse flex-md-wrap">
            <div class="col-12" :class="{'col-md-6': answers.length}">
                <HubspotForm
                    :portal-id="portalId"
                    :form-id="formId"
                    @ready="onFormReady"
                    @submitted="onFormSubmitted"
                />
            </div>
            <div v-if="answers.length" class="col-12 col-md-6">
                <div class="summary p-4">
                    <div v-if="answers.length" class="mb-4">
                        <h4 class="border-bottom">{{ answersText }}</h4>
                        <ol>
                            <li
                                v-for="(answer, i) in answers"
                                :key="answer.answerId"
                                class="row"
                            >
                                <span class="col-6 mb-0">{{ i + 1 }}.&nbsp;{{ answer.stepName.toUpperCase() }}</span>
                                <span class="col-6 mb-0">{{ answer.answer }}</span>
                            </li>
                        </ol>
                    </div>
                    <div v-if="results.length > 0">
                        <h4 class="border-bottom">{{ resultsText }}</h4>
                        <ul>
                            <li v-for="model in results" :key="model.Id">
                                <span>{{ model.Name }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>